import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { Notification } from '@/types';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isSidebarOpen: boolean;
  selectedNotificationIds: string[];
  isLoading: boolean;
}

interface NotificationActions {
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  updateNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
  removeMultipleNotifications: (ids: string[]) => void;
  removeNotificationsByTicketId: (ticketId: string) => void;
  setUnreadCount: (count: number) => void;
  incrementUnreadCount: () => void;
  decrementUnreadCount: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  selectNotification: (id: string) => void;
  deselectNotification: (id: string) => void;
  toggleSelection: (id: string) => void;
  selectAll: (ids: string[]) => void;
  deselectAll: () => void;
  setLoading: (loading: boolean) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

export const useNotificationStore = create<NotificationState & NotificationActions>()(
  devtools(
    persist(
      (set, _get) => ({
        // State
        notifications: [],
        unreadCount: 0,
        isSidebarOpen: false,
        selectedNotificationIds: [],
        isLoading: false,

        // Actions
        setNotifications: (notifications) => {
          // Replace with API response (e.g. after fetch); removes orphaned/deleted-ticket notifications
          const unreadCount = notifications.filter((n) => !n.read).length;
          set({ notifications, unreadCount });
        },

        addNotification: (notification) => {
          set((state) => {
            // Check if notification already exists
            if (state.notifications.some((n) => n.id === notification.id)) {
              return state;
            }
            return {
              notifications: [notification, ...state.notifications],
              unreadCount: notification.read ? state.unreadCount : state.unreadCount + 1,
            };
          });
        },

        updateNotification: (notification) => {
          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.id === notification.id ? notification : n
            ),
          }));
        },

        removeNotification: (id) => {
          set((state) => {
            const notification = state.notifications.find((n) => n.id === id);
            return {
              notifications: state.notifications.filter((n) => n.id !== id),
              unreadCount: notification && !notification.read
                ? Math.max(0, state.unreadCount - 1)
                : state.unreadCount,
              selectedNotificationIds: state.selectedNotificationIds.filter((selectedId) => selectedId !== id),
            };
          });
        },

        removeMultipleNotifications: (ids) => {
          set((state) => {
            const removedNotifications = state.notifications.filter((n) => ids.includes(n.id));
            const unreadRemoved = removedNotifications.filter((n) => !n.read).length;
            return {
              notifications: state.notifications.filter((n) => !ids.includes(n.id)),
              unreadCount: Math.max(0, state.unreadCount - unreadRemoved),
              selectedNotificationIds: state.selectedNotificationIds.filter((id) => !ids.includes(id)),
            };
          });
        },

        removeNotificationsByTicketId: (ticketId) => {
          set((state) => {
            const removed = state.notifications.filter((n) => n.ticketId === ticketId);
            const unreadRemoved = removed.filter((n) => !n.read).length;
            return {
              notifications: state.notifications.filter((n) => n.ticketId !== ticketId),
              unreadCount: Math.max(0, state.unreadCount - unreadRemoved),
              selectedNotificationIds: state.selectedNotificationIds.filter(
                (id) => !removed.some((n) => n.id === id)
              ),
            };
          });
        },

        setUnreadCount: (count) => set({ unreadCount: count }),

        incrementUnreadCount: () => set((state) => ({ unreadCount: state.unreadCount + 1 })),

        decrementUnreadCount: () => set((state) => ({ unreadCount: Math.max(0, state.unreadCount - 1) })),

        openSidebar: () => set({ isSidebarOpen: true }),

        closeSidebar: () => set({ isSidebarOpen: false }),

        toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

        selectNotification: (id) => {
          set((state) => ({
            selectedNotificationIds: [...state.selectedNotificationIds, id],
          }));
        },

        deselectNotification: (id) => {
          set((state) => ({
            selectedNotificationIds: state.selectedNotificationIds.filter((selectedId) => selectedId !== id),
          }));
        },

        toggleSelection: (id) => {
          set((state) => {
            if (state.selectedNotificationIds.includes(id)) {
              return {
                selectedNotificationIds: state.selectedNotificationIds.filter((selectedId) => selectedId !== id),
              };
            }
            return {
              selectedNotificationIds: [...state.selectedNotificationIds, id],
            };
          });
        },

        selectAll: (ids) => set({ selectedNotificationIds: ids }),

        deselectAll: () => set({ selectedNotificationIds: [] }),

        setLoading: (loading) => set({ isLoading: loading }),

        markAsRead: (id) => {
          set((state) => {
            const notification = state.notifications.find((n) => n.id === id);
            if (notification && !notification.read) {
              return {
                notifications: state.notifications.map((n) =>
                  n.id === id ? { ...n, read: true } : n
                ),
                unreadCount: Math.max(0, state.unreadCount - 1),
              };
            }
            return state;
          });
        },

        markAllAsRead: () => {
          set((state) => ({
            notifications: state.notifications.map((n) => ({ ...n, read: true })),
            unreadCount: 0,
          }));
        },
      }),
      {
        name: 'notification-storage',
        partialize: (state) => ({
          notifications: state.notifications,
          unreadCount: state.unreadCount,
        }),
      }
    )
  )
);
